import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import BaseGrid from '../BaseGrid';
import GridLoader from '../../GridLoader';
import GridErrorMessage from '../../GridErrorMessage';
import useGridData from '../../hooks/useGridData';
import useSortPageState from 'shared/grids/hooks/useSortPageState';
import { requestStatuses } from 'shared/../constants/store';
import { gridDataHandlingStrategies } from 'shared/../constants/gridDataHandlingStrategies';
import { getFilteredData } from 'src/fetch/filtering';
import './style.scss';
import { initialFilter } from '../../../filter/constants';

const propPath = require('property-path');

const PageGrid = ({
  name,
  getData,
  columns,
  searchParams,
  isLoading,
  initialSort,
  initialPage,
  reloadOnSort,
  onRowSelect,
  needReload = false,
  onReload,
  onLoaded,
  onItemChange,
  editField,
  itemInEdit,
  identifierColumn,
  onChangesSaved,
  expandField,
  emptyObjectFactory,
  customChangedHandlers = {},
  setInEdit,
  onAddMultiple,
  dataHandlingStrategy = gridDataHandlingStrategies.DEFAULT,
  cellRender,
  dataState,
  onDataStateChange,
  process,
  columnsToDisplay,
  filteringOptions,
  useSearchQuery = true,
  ...props
}, ref) => {
  const filterRef = useRef(null);

  const getDataWithFilter = (params) => {
    if (!filterRef.current || !filteringOptions) {
      return getData(params);
    }
    return getFilteredData(filteringOptions.entity, filterRef.current, filteringOptions.paginated, params);
  };

  const {
    sort, onSortChange, page, onPageChange, sortParams, pageParams,
  } = useSortPageState({
    initialSort,
    initialPage: initialPage ?? { skip: 0, take: 25 },
    isMultiSorting: false,
    columns,
  });

  const {
    status,
    items,
    total,
    error,
    setItems,
    reload,
  } = useGridData({
    getData: getDataWithFilter,
    searchParams,
    reloadOnSort,
    isLocalData: !reloadOnSort,
    dataHandlingStrategy,
    process,
    dataState,
    sort,
    page,
    onPageChange,
    sortParams,
    pageParams,
  });

  const [originalItems, setOriginalItems] = useState([]);
  const [editEnabled, setEditEnabled] = useState(false);
  const [changes, setChanges] = useState({});
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [filterApplied, setFilterApplied] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(initialFilter);

  useImperativeHandle(ref, () => ({
    deleteItems: (columnName, valuesToDelete) => {
      const newItems = items.filter(x => valuesToDelete.indexOf(x[columnName]) === -1);
      setItems(newItems);
    }
  }));

  const handleRowSelected = (dataItem) => {
    if (onRowSelect) {
      onRowSelect(dataItem);
    }
  };

  const handleFilterApplied = (filter) => {
    filterRef.current = filter.expression;
    setFilterApplied(true);
    setCurrentFilter(filter);
    reload();
  }

  const handleClearFilter = () => {
    filterRef.current = null;
    setFilterApplied(false);
    setCurrentFilter(initialFilter);
    reload();
  }

  const handleItemChanged = (event) => {

    const field = event.field || '';
    const dataItem = event.dataItem;
    const identifierColumnName = dataItem.isNew ? 'tempId' : identifierColumn;
    const currentDataItemId = dataItem[identifierColumnName];
    const hasToBeDeleted = field === '_deleted' && event.value;

    let newItems = hasToBeDeleted ? items.filter(x => x[identifierColumnName] !== currentDataItemId) : items.map(item => {

      if (item[identifierColumnName] === currentDataItemId) {

        const newItem = JSON.parse(JSON.stringify(item))
        propPath.set(newItem, field, event.value);
        return newItem;
      }

      return item;
    });


    if (!dataItem.isNew && !hasToBeDeleted) {
      const originalItem = originalItems.find(x => x[identifierColumn] === currentDataItemId);
      const changesKey = `${currentDataItemId}:${field}`;

      if (propPath.get(originalItem, field) !== event.value) {

        changes[changesKey] = {
          id: currentDataItemId,
          field: field.charAt(0).toUpperCase() + field.slice(1),
          value: event?.value.value || event?.value
        };
      }
      else {
        delete changes[changesKey];
      }
    }

    if (customChangedHandlers[field]) {
      customChangedHandlers[field].forEach(handler => {

        let handleResult = handler(dataItem, event.value, newItems);
        newItems = handleResult.newItems;

        if (!dataItem.isNew) {
          handleResult.changes.forEach(c => {
            const changesKey = `${currentDataItemId}:${c.field.toLowerCase()}`;
            changes[changesKey] = c;
          });
        }
      });
    }


    if (hasToBeDeleted) {

      setItemsToDelete([
        ...itemsToDelete,
        currentDataItemId
      ]);
    }

    setChanges(changes);
    setItems(newItems);

    if (onItemChange) {
      onItemChange(event);
    }
  };

  const handleChangesDiscarded = (event) => {
    event.preventDefault();

    setItems(originalItems);
    setChanges({});
    setEditEnabled(false);

    if (setInEdit) {
      setInEdit(false);
    }
  };

  const handleExpandChanged = (event) => {

    setItems(
      items.map((item) => {
        if (item[identifierColumn] === event.dataItem[identifierColumn]) {
          item.expanded = 'expanded' in item ? !event.dataItem.expanded : false;
        }

        return item;
      }),
    );
  };

  const handleChangesSaved = (event) => {
    event.preventDefault();

    setEditEnabled(false);
    if (setInEdit) {
      setInEdit(false);
    }
    onChangesSaved({
      entityChanges: Object.values(changes),
      newEntities: items.filter(x => x.isNew),
      itemsToDelete
    });

    let editItems = items.map(item => ({
      ...item,
      inEdit: false
    }));
    setItems(editItems);
  };

  const handleAdd = () => {

    if (!emptyObjectFactory) {

      throw new Error('Empty object is not specified.');
    }

    setItems(prev => {
      const newItems = [...prev];

      newItems.push({
        ...emptyObjectFactory(),
        inEdit: true
      });
      return newItems;
    });
  };

  const getItemEditState = (item) => {

    if (item.isNew) {
      return true;
    }

    return 'inEdit' in item ? !item.inEdit : true;
  };


  const handleEdit = (event) => {

    event.preventDefault();
    setItems(prev => {

      let editItems = prev.map(item => ({
        ...item,
        inEdit: getItemEditState(item)
      }));

      return editItems;
    });

    setEditEnabled(prev => !prev);
  };


  useEffect(() => {

    if (!filteringOptions) {
      return;
    }

    const savedFilter = window.sessionStorage.getItem(`${filteringOptions.entity}.filter`);

    if (savedFilter) {
      handleFilterApplied(JSON.parse(savedFilter));
      window.sessionStorage.removeItem(`${filteringOptions.entity}.filter`)
    }

  }, []);

  useEffect(() => {
    if (status === requestStatuses.SUCCESS) {

      if (editField) {
        setOriginalItems(items);
      }

      if (onLoaded) {
        onLoaded(items, searchParams);
      }
    }
  }, [status]);

  useEffect(() => {
    if (needReload) {
      reload();
      onReload();
    }
  }, [needReload]);

  useEffect(() => {
    let editItems = items.map(item => ({
      ...item,
      inEdit: itemInEdit && item[identifierColumn] === itemInEdit[identifierColumn]
    }));
    setEditEnabled(!!itemInEdit);

    if (setInEdit) {
      setInEdit(!!itemInEdit);
    }

    setItems(editItems);
  }, [itemInEdit]);

  if (status === requestStatuses.INIT || status === requestStatuses.PENDING || isLoading) {
    return (
      <div className="old-page-grid">
        <GridLoader />
      </div>
    );
  }

  if (status === requestStatuses.FAILED) {
    return (
      <GridErrorMessage name={name} error={error} />
    );
  }

  if (status === requestStatuses.SUCCESS) {
    return (
      <div className="old-page-grid">
        <BaseGrid
          resizable
          name={name}
          data={items}
          columns={columns}
          sortable
          sort={sort}
          onSortChange={onSortChange}
          skip={page.skip}
          pageSize={page.take}
          total={total}
          onPageChange={onPageChange}
          onRowSelect={handleRowSelected}
          editField={editField}
          onItemChange={handleItemChanged}
          onChangesDiscarded={handleChangesDiscarded}
          onChangesSaved={handleChangesSaved}
          editEnabled={editEnabled}
          expandField={expandField}
          onExpandChange={expandField ? handleExpandChanged : null}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onAddMultiple={onAddMultiple}
          cellRender={cellRender}
          onDataStateChange={onDataStateChange}
          columnsToDisplay={columnsToDisplay}
          filteringOptions={filteringOptions}
          onFilterClear={handleClearFilter}
          onFilterApply={handleFilterApplied}
          filterApplied={filterApplied}
          currentFilter={currentFilter}
          useSearchQuery={useSearchQuery}
          {...props}
        />
      </div>
    );
  }
};

export default forwardRef(PageGrid);
